import type { AdminCommandType } from "./admin/use-admin-command";
import type { GovernanceCommandType } from "./governance/schemas";

export type CommandType = AdminCommandType | GovernanceCommandType;

export interface CommandWording {
  pending: string;
  success: string;
}

// What the feedback strip says while a command runs and once it has applied.
// Keyed by command type so the words follow what was actually submitted: a
// delete says "Deleted", never the "Saved" a shared default used to show.
//
// `satisfies Record<CommandType, …>` makes this exhaustive. A new admin or
// governance command type fails the type check here until it has its own
// words, so it cannot fall back to someone else's.
const WORDING = {
  // Event admin commands (events/{eventId}/admin_commands)
  ADD_EVENT_ATTENDEE: { pending: "Adding…", success: "Added" },
  ASSIGN_ATTENDEE_TRACKS: {
    pending: "Updating tracks…",
    success: "Tracks updated",
  },
  DECIDE_TRACK_SWITCH: { pending: "Answering…", success: "Answered" },
  DECIDE_SEAT_REQUEST: { pending: "Answering…", success: "Answered" },
  DELETE_FEEDBACK_FORM: { pending: "Deleting form…", success: "Form deleted" },
  DELETE_RESOURCE: { pending: "Deleting…", success: "Deleted" },
  DELETE_SEATING: {
    pending: "Deleting seating…",
    success: "Seating deleted",
  },
  SUGGEST_SEATING: {
    pending: "Suggesting a seating…",
    success: "Seating suggested. Pinned guests stayed where they were.",
  },
  PUBLISH_SEATING: {
    pending: "Publishing seating…",
    success: "Seating published. Guests now see their dinner table.",
  },
  UNPUBLISH_SEATING: {
    pending: "Unpublishing seating…",
    success: "Seating unpublished. Guests no longer see it.",
  },
  DELETE_SPEAKER: { pending: "Deleting…", success: "Deleted" },
  DELETE_TRACK: { pending: "Deleting…", success: "Deleted" },
  DELETE_VENUE: { pending: "Deleting…", success: "Deleted" },
  DISMISS_IMPORT_ISSUES: { pending: "Dismissing…", success: "Dismissed" },
  IMPORT_ATTENDEES: { pending: "Importing…", success: "Imported" },
  RESCHEDULE_AGENDA: { pending: "Rescheduling…", success: "Rescheduled" },
  RESOLVE_IMPORT_ISSUE: { pending: "Retrying…", success: "Row imported" },
  RESTORE_EVENT_ATTENDEE: { pending: "Restoring…", success: "Restored" },
  SEND_EVENT_ANNOUNCEMENT: {
    pending: "Sending…",
    success: "Sent to everyone registered for this event",
  },
  SCHEDULE_EVENT_ANNOUNCEMENT: {
    pending: "Scheduling…",
    success: "Scheduled. You can edit or cancel it below until it goes out.",
  },
  UPDATE_SCHEDULED_ANNOUNCEMENT: {
    pending: "Saving changes…",
    success: "Scheduled announcement updated",
  },
  CANCEL_SCHEDULED_ANNOUNCEMENT: {
    pending: "Cancelling…",
    success: "Cancelled. It will not be sent.",
  },
  DELETE_EVENT_ANNOUNCEMENT: {
    pending: "Deleting from everyone's inbox…",
    success: "Announcement deleted",
  },
  SAVE_FEEDBACK_FORM: { pending: "Saving form…", success: "Form saved" },
  SAVE_AFTER_EVENT_SUMMARY: {
    pending: "Saving summary…",
    success: "Summary saved",
  },
  SET_EVENT_SLUG: { pending: "Saving…", success: "Saved" },
  UPDATE_EVENT_DESCRIPTION: { pending: "Saving…", success: "Saved" },
  // Both channels: an event admin and a governance user mint and revoke alike.
  MINT_INVITE: { pending: "Creating invite…", success: "Invite created" },
  REVOKE_INVITE: { pending: "Revoking…", success: "Revoked" },
  // Governance commands (governance_commands)
  BULK_DELETE_ATTENDEES: { pending: "Deleting…", success: "Deleted" },
  CREATE_EVENT: { pending: "Creating event…", success: "Event created" },
  DELETE_USER: { pending: "Deleting user…", success: "User deleted" },
  SET_DOMAIN_ALLOWLIST: {
    pending: "Updating allowed domains…",
    success: "Allowed domains updated",
  },
  SET_EVENT_ADMIN: {
    pending: "Updating event admins…",
    success: "Event admins updated",
  },
  SET_PLATFORM_ROLE: { pending: "Changing role…", success: "Role changed" },
} as const satisfies Record<CommandType, CommandWording>;

export function commandWording(type: CommandType): CommandWording {
  return WORDING[type];
}
